// src/components/ProtectedRoute.tsx
// HomeQuest Tech - Protected Route Component

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: 'admin' | 'builder' | 'vendor';
}

interface UserProfile {
  id: string;
  role: string;
  team_id: string | null;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireRole }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check localStorage for cached session
        const cachedToken = localStorage.getItem('sb-fbwmkkskdrvaipmkddwm-auth-token');
        if (cachedToken) {
          try {
            const cached = JSON.parse(cachedToken);
            if (cached.access_token) {
              console.log('Found cached session, using it immediately');
              setIsAuthenticated(true);
              // Continue with full auth check in background
            }
          } catch (e) {
            console.error('Failed to parse cached token');
          }
        }

        // Increased timeout to 10 seconds with fallback to cache
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Auth check timeout')), 10000)
        );

        console.log('Checking Supabase session...');

        try {
          // Add timeout to Supabase auth check
          const sessionCheck = supabase.auth.getSession();
          const { data: { session }, error: sessionError } = await Promise.race([
            sessionCheck,
            timeoutPromise
          ]) as Awaited<typeof sessionCheck>;

          if (sessionError) {
            console.error('Session error:', sessionError);
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }

          if (!session) {
            console.log('No active session found');
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }

          console.log('Session found for:', session.user.email);
          setIsAuthenticated(true);

          // Check if user is the owner
          const ownerEmail = 'kentrill@yhshomes.com';
          const userIsOwner = session.user.email === ownerEmail;
          setIsOwner(userIsOwner);

          // Get full user profile for all users (not just when requireRole is set)
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, role, team_id, full_name, phone_number, email')
            .eq('id', session.user.id)
            .single();

          if (profileError) {
            console.error('Error loading profile:', profileError);
            // Cache minimal user data if profile fetch fails
            localStorage.setItem('user', JSON.stringify({
              id: session.user.id,
              email: session.user.email,
              role: 'builder'
            }));
          } else {
            // Cache the full user profile for faster loads
            localStorage.setItem('user', JSON.stringify({
              id: profile.id,
              email: profile.email || session.user.email,
              full_name: profile.full_name,
              phone_number: profile.phone_number,
              role: profile.role || 'builder',
              team_id: profile.team_id,
              department: 'Billing' // Default department, update from team_members if needed
            }));

            // Always set user profile (not just when requireRole is specified)
            setUserProfile(profile);

            // Check subscription status (skip for owner)
            if (!userIsOwner && profile.team_id) {
              console.log('🔍 Checking subscription for team_id:', profile.team_id);

              const { data: subscription, error: subError } = await supabase
                .from('subscriptions')
                .select('status, tier')
                .eq('team_id', String(profile.team_id))
                .eq('status', 'active')
                .maybeSingle();

              if (subError) {
                console.error('❌ Subscription query error:', subError);
              }

              setHasActiveSubscription(!!subscription);
              console.log('📊 Subscription check result:', {
                found: !!subscription,
                tier: subscription?.tier,
                status: subscription?.status,
                team_id: profile.team_id
              });
            } else if (userIsOwner) {
              // Owner always has access
              setHasActiveSubscription(true);
              console.log('👑 Owner detected - bypassing subscription check');
            }
          }
        } catch (timeoutError) {
          console.error('Auth check timed out:', timeoutError);
          console.log('Supabase is slow, using cached session only (no additional DB calls)...');

          // Try to recover from timeout by using ONLY cached session - NO more Supabase calls
          const cachedSession = localStorage.getItem('sb-fbwmkkskdrvaipmkddwm-auth-token');
          const cachedUser = localStorage.getItem('user');

          if (cachedSession) {
            try {
              const sessionData = JSON.parse(cachedSession);

              if (sessionData.user?.id) {
                console.log('Using cached session for user:', sessionData.user.email);

                // Check if user is owner
                const ownerEmail = 'kentrill@yhshomes.com';
                const userIsOwner = sessionData.user.email === ownerEmail;
                setIsOwner(userIsOwner);

                // DO NOT make any more Supabase calls - just use cached data
                setIsAuthenticated(true);

                // Owner always has subscription access
                if (userIsOwner) {
                  setHasActiveSubscription(true);
                  console.log('👑 Owner detected in cached session - granting access');
                } else {
                  // For non-owners, check if they just completed payment
                  const justCompletedPayment = localStorage.getItem('subscription_just_created');
                  if (justCompletedPayment) {
                    const timestamp = parseInt(justCompletedPayment);
                    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

                    if (timestamp > fiveMinutesAgo) {
                      setHasActiveSubscription(true);
                      console.log('⚡ Recently completed payment - granting temporary access');
                    } else {
                      setHasActiveSubscription(false);
                      console.log('⚠️ No recent payment found - will check subscription on next load');
                    }
                  } else {
                    setHasActiveSubscription(false);
                    console.log('⚠️ No subscription verified - need to check database');
                  }
                }

                // Use cached user data if available, otherwise use session data
                if (cachedUser) {
                  try {
                    const userData = JSON.parse(cachedUser);
                    setUserProfile({
                      id: userData.id || sessionData.user.id,
                      role: userData.role || 'builder',
                      team_id: userData.team_id || null
                    });
                    console.log('Using cached user profile');
                  } catch (e) {
                    // Fallback to session data only
                    setUserProfile({
                      id: sessionData.user.id,
                      role: 'builder',
                      team_id: null
                    });
                  }
                } else {
                  // No cached user profile, use session data
                  setUserProfile({
                    id: sessionData.user.id,
                    role: 'builder',
                    team_id: null
                  });
                }
              } else {
                console.log('No valid user in cached session');
                setIsAuthenticated(false);
              }
            } catch (e) {
              console.error('Error parsing cached session:', e);
              setIsAuthenticated(false);
            }
          } else {
            // No cached session at all
            console.warn('No cached auth found, redirecting to login');
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setIsAuthenticated(false);
        setUserProfile(null);
      } else if (event === 'SIGNED_IN' && session) {
        setIsAuthenticated(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [requireRole]);

  // Loading state
  if (loading) {
    return (
      <div className="protected-route-loading">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading HomeQuest...</p>
          <p style={{ fontSize: '0.85em', color: '#888', marginTop: '0.5rem' }}>
            Connecting to secure servers...
          </p>
        </div>
        
        <style>{`
          .protected-route-loading {
            min-height: 100vh;
            background: #0f0f23;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .loading-container {
            text-align: center;
          }
          
          .spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #333;
            border-top-color: #4a90e2;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 1rem;
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check subscription (except for owner and certain routes)
  const allowedWithoutSubscription = ['/pricing', '/payment/success', '/payment/cancel'];
  const isAllowedRoute = allowedWithoutSubscription.some(route => location.pathname.startsWith(route));

  // Check if user is a team member (has team_id but is not owner)
  const isTeamMember = userProfile?.team_id && !isOwner;

  console.log('🔍 SUBSCRIPTION CHECK DEBUG:', {
    isOwner,
    hasActiveSubscription,
    isTeamMember,
    teamId: userProfile?.team_id,
    isAllowedRoute,
    currentPath: location.pathname,
    willRedirect: !isOwner && !hasActiveSubscription && !isAllowedRoute && !isTeamMember
  });

  // Only redirect to pricing if:
  // 1. Not the owner
  // 2. No active subscription
  // 3. Not on an allowed route
  // 4. Not a team member (team members inherit team subscription)
  if (!isOwner && !hasActiveSubscription && !isAllowedRoute && !isTeamMember) {
    console.warn('⚠️ No active subscription - redirecting to pricing');
    console.log('Check localStorage flag:', localStorage.getItem('subscription_just_created'));
    return <Navigate to="/pricing" replace />;
  }

  // Check role requirement
  if (requireRole && userProfile) {
    if (userProfile.role !== requireRole) {
      // Redirect based on user's actual role
      if (userProfile.role === 'admin') {
        return <Navigate to="/admin" replace />;
      } else if (userProfile.role === 'vendor') {
        return <Navigate to="/vendors" replace />;
      } else {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  // All checks passed - render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;